import { lazy, Suspense } from "react"
import { createBrowserRouter } from "react-router-dom"

import { preloadMeetingProfile } from "@/hooks/useMeetingProfile"
import { preloadMeetingSessionDetails } from "@/hooks/useMeetingSessionDetails"
import { lazyWithPreload } from "@/utils/lazyWithPreload"

import { pointsApi } from "./api"
import { DesktopRoute } from "./components/common/DesktopRoute"
import { RouteErrorBoundary } from "./components/common/RouteErrorBoundary/RouteErrorBoundary"
import { PublicLayout } from "./components/PublicLayout/PublicLayout"
import { getInviteCode } from "./hooks/useInviteCode"
import { logger } from "./lib/logger"
import {
  ensureBillingPlans,
  ensureBillingSummary,
  setLeaderboard,
  store,
} from "./store"
import { ensureActiveWorkspace, ensureMembers } from "./store/workspace/slice"

const AppLayout = lazy(() =>
  import("./components/AppLayout/AppLayout").then((m) => ({
    default: m.AppLayout,
  })),
)
const LazyProtectedRoute = lazy(() =>
  import("./components/common/ProtectedRoute").then((m) => ({
    default: m.ProtectedRoute,
  })),
)
const LazyGuestRoute = lazy(() =>
  import("./components/common/GuestRoute").then((m) => ({
    default: m.GuestRoute,
  })),
)

const Calls = lazyWithPreload(() => import("./pages/Calls/Calls"))
const Meetings = lazyWithPreload(() => import("./pages/Meetings/Meetings"))
const Profile = lazyWithPreload(() => import("./pages/Profile/Profile"))
const Scheduling = lazyWithPreload(
  () => import("./pages/Scheduling/Scheduling"),
)
const CreateYourFirstMeetingType = lazyWithPreload(
  () =>
    import("./pages/Scheduling/components/CreateYourFirstMeetingType/CreateYourFirstMeetingType"),
)
const UserProfile = lazyWithPreload(() =>
  import("./pages/UserProfile/UserProfile").then((module) => ({
    default: module.UserProfile,
  })),
)
const Booking = lazyWithPreload(() => import("./pages/Booking/Booking"))
const UserProfileById = lazyWithPreload(() =>
  import("./pages/UserProfile/UserProfile").then((module) => ({
    default: module.UserProfileById,
  })),
)
const EditProfile = lazyWithPreload(
  () => import("./pages/EditProfile/EditProfile"),
)
const StartRoom = lazyWithPreload(() => import("./pages/StartRoom/StartRoom"))
const Call = lazyWithPreload(() => import("./pages/Call/Call"))
const RoomSummary = lazyWithPreload(
  () => import("./pages/RoomSummary/RoomSummary"),
)

const RoomPage = lazyWithPreload(() => import("./pages/Room/Room"))
const JoinRoomPage = lazyWithPreload(() => import("./pages/JoinRoom/JoinRoom"))
const PrivacyPolicy = lazyWithPreload(
  () => import("./pages/policies/PrivacyPolicyNew/PrivacyPolicyNew"),
)
const TermsOfService = lazyWithPreload(
  () => import("./pages/policies/TermsOfServiceNew/TermsOfServiceNew"),
)
const AffiliateTerms = lazyWithPreload(
  () => import("./pages/policies/AffiliateTermsNew/AffiliateTermsNew"),
)
const AffiliateProgram = lazyWithPreload(
  () => import("./pages/AffiliateProgram/AffiliateProgram"),
)
const Leaderboard = lazyWithPreload(
  () => import("./pages/Leaderboard/Leaderboard"),
)
const MeetingForm = lazyWithPreload(
  () => import("./pages/MeetingForm/MeetingForm"),
)
const MeetingDetail = lazyWithPreload(
  () => import("./pages/MeetingDetail/MeetingDetail"),
)
const SessionSummary = lazyWithPreload(
  () => import("./pages/SessionSummary/SessionSummary"),
)
const RoomInfoPage = lazyWithPreload(
  () => import("./pages/RoomInfoPage/RoomInfoPage"),
)
const RecordingView = lazyWithPreload(
  () => import("./pages/RecordingView/RecordingView"),
)
const ReportInvitation = lazyWithPreload(
  () => import("./pages/ReportInvitation/ReportInvitation"),
)
const ReportWorkspaceUser = lazyWithPreload(
  () => import("./pages/ReportWorkspaceUser/ReportWorkspaceUser"),
)
const UnsubscribeFromEmails = lazyWithPreload(
  () => import("./pages/UnsubscribeFromEmails/UnsubscribeFromEmails"),
)
const BillingPage = lazyWithPreload(
  () => import("./pages/BillingPage/BillingPage"),
)
const ChatPage = lazyWithPreload(() => import("./pages/Chat/views/ChatPage"))
const LandingPage = lazyWithPreload(
  () => import("./pages/LandingPage/LandingPage"),
)
const Home = lazyWithPreload(() => import("./pages/Home/Home"))
const TeamBillingPage = lazyWithPreload(
  () => import("./pages/TeamBillingPage/TeamBillingPage"),
)
const InvitationPage = lazyWithPreload(
  () => import("./pages/InvitationPage/InvitationPage"),
)
const TestModals = lazyWithPreload(
  () => import("./pages/TestModals/TestModals"),
)
const TestIcons = lazyWithPreload(() => import("./pages/TestIcons/TestIcons"))
const TestRtl = lazyWithPreload(() => import("./pages/TestRtl/TestRtl"))
const MeetingTypeCreatePage = lazyWithPreload(
  () => import("./pages/Scheduling/MeetingTypeCreatePage"),
)
const MeetingTypeEditPage = lazyWithPreload(
  () => import("./pages/Scheduling/MeetingTypeEditPage"),
)
const ProfilePreview = lazyWithPreload(
  () => import("./pages/UserProfile/ProfilePreview"),
)
const BookedMeetingPage = lazyWithPreload(
  () => import("./pages/BookedMeeting/BookedMeetingPage"),
)

import { type RoutePath } from "./routePaths"

interface RouteConfig {
  path: RoutePath
  component: React.ComponentType
  protected?: boolean // default: true
  mobileAllowed?: boolean // default: false
  guestOnly?: boolean // default: false — mobile guest flow guard (see GuestRoute)
}

const routes: RouteConfig[] = [
  {
    path: "/",
    component: Home,
    protected: false,
  },
  {
    path: "/meetings",
    component: Meetings,
    protected: true,
  },
  {
    path: "/calls",
    component: Calls,
    protected: true,
  },
  {
    path: "/profile",
    component: Profile,
    protected: true,
  },
  {
    path: "/scheduling",
    component: Scheduling,
    protected: true,
  },
  {
    path: "/scheduling/create-your-first-meeting-type",
    component: CreateYourFirstMeetingType,
    protected: true,
  },
  {
    path: "/scheduling/create",
    component: MeetingTypeCreatePage,
    protected: true,
  },
  {
    path: "/scheduling/edit/:meetingTypeId",
    component: MeetingTypeEditPage,
    protected: true,
  },
  {
    path: "/scheduling/preview",
    component: ProfilePreview,
    protected: true,
  },
  {
    path: "/settings",
    component: EditProfile,
    protected: true,
  },
  {
    path: "/start-room",
    component: StartRoom,
    protected: false,
  },
  {
    path: "/start-room/:roomId",
    component: StartRoom,
    protected: false,
  },
  {
    path: "/call/:callId",
    component: Call,
    protected: true,
  },
  {
    path: "/room-summary/:roomId",
    component: RoomSummary,
    protected: false,
    mobileAllowed: true,
    guestOnly: true,
  },
  {
    path: "/room-summary/s/:sessionId",
    component: RoomSummary,
    protected: false,
    mobileAllowed: true,
    guestOnly: true,
  },

  {
    path: "/leaderboard",
    component: Leaderboard,
    protected: false,
  },
  {
    path: "/join/:roomId",
    component: JoinRoomPage,
    protected: false,
    mobileAllowed: true,
    guestOnly: true,
  },
  {
    path: "/u/:username",
    component: UserProfile,
    protected: false,
    mobileAllowed: true,
  },
  {
    path: "/u/:username/:slug",
    component: Booking,
    protected: false,
    mobileAllowed: true,
  },
  {
    path: "/booking/:bookedMeetingId",
    component: BookedMeetingPage,
    protected: false,
    mobileAllowed: true,
  },
  {
    path: "/booking/:bookedMeetingId/reschedule",
    component: Booking,
    protected: false,
    mobileAllowed: true,
  },
  {
    path: "/user/:userId",
    component: UserProfileById,
    protected: false,
  },
  {
    path: "/privacy-policy",
    component: PrivacyPolicy,
    protected: false,
    mobileAllowed: true,
  },
  {
    path: "/terms-of-service",
    component: TermsOfService,
    protected: false,
    mobileAllowed: true,
  },
  {
    path: "/affiliate-terms",
    component: AffiliateTerms,
    protected: false,
    mobileAllowed: true,
  },
  {
    path: "/affiliate-program",
    component: AffiliateProgram,
    protected: false,
    mobileAllowed: true,
  },
  {
    path: "/schedule-new-meeting",
    component: MeetingForm,
    protected: true,
  },
  {
    path: "/edit-meeting/:meetId",
    component: MeetingForm,
    protected: true,
  },
  {
    path: "/meeting/:meetId",
    component: MeetingDetail,
    protected: true,
  },
  {
    path: "/m/:sessionId",
    component: SessionSummary,
    protected: false,
  },
  {
    path: "/r/:roomId",
    component: RoomInfoPage,
    protected: true,
  },
  {
    path: "/rec/:id",
    component: RecordingView,
    protected: false,
  },
  {
    path: "/calendar/report-organizer",
    component: ReportInvitation,
    protected: false,
  },
  {
    path: "/workspace/report-user",
    component: ReportWorkspaceUser,
    protected: false,
  },
  {
    path: "/calendar/unsubscribe-from-emails",
    component: UnsubscribeFromEmails,
    protected: false,
    mobileAllowed: true,
  },
  {
    path: "/billing",
    component: BillingPage,
    protected: true,
    mobileAllowed: true,
  },
  {
    path: "/billing/success",
    component: BillingPage,
    protected: true,
    mobileAllowed: true,
  },
  {
    path: "/billing/credits-success",
    component: BillingPage,
    protected: true,
    mobileAllowed: true,
  },
  {
    path: "/chat",
    component: ChatPage,
    protected: true,
  },
  {
    path: "/chat/:userId",
    component: ChatPage,
    protected: true,
  },
  {
    path: "/team-billing/:workspaceId",
    component: TeamBillingPage,
    protected: true,
    mobileAllowed: true,
  },
  {
    path: "/workspace/invite/:invitationId",
    component: InvitationPage,
    protected: false,
    mobileAllowed: false,
  },
  ...(import.meta.env.VITE_TEST_ENVIRONMENT === "true"
    ? [
        {
          path: "/test-modals" as const,
          component: TestModals,
          protected: false,
          mobileAllowed: true,
        },
        {
          path: "/test-icons" as const,
          component: TestIcons,
          protected: false,
          mobileAllowed: true,
        },
        {
          path: "/test-rtl" as const,
          component: TestRtl,
          protected: false,
          mobileAllowed: true,
        },
      ]
    : []),
  {
    path: "/:roomId",
    component: RoomPage,
    protected: false,
    mobileAllowed: true,
    guestOnly: true,
  },
]

const BrandPage = lazyWithPreload(() => import("./pages/BrandPage/BrandPage"))

const publicRoutes: RouteConfig[] = [
  {
    path: "/about",
    component: LandingPage,
    protected: false,
    mobileAllowed: true,
  },
  {
    path: "/brand",
    component: BrandPage,
    protected: false,
    mobileAllowed: true,
  },
]

let lastLeaderboardPreload = 0
const LEADERBOARD_THROTTLE_MS = 10_000

const preloadLeaderboard = () => {
  Leaderboard.preload()
  if (Date.now() - lastLeaderboardPreload < LEADERBOARD_THROTTLE_MS) return
  lastLeaderboardPreload = Date.now()
  pointsApi
    .getLeaderboard({ refCode: getInviteCode() })
    .then((data) => {
      store.dispatch(
        setLeaderboard({ records: data.top, refCode: data.refCode }),
      )
    })
    .catch((error) => {
      logger.error("[preloadRoute] Failed to preload leaderboard data", error)
      lastLeaderboardPreload = 0
    })
}

const preloadBillingRoute = () => {
  BillingPage.preload()

  void Promise.all([
    store.dispatch(ensureBillingPlans()),
    store.dispatch(ensureBillingSummary()),
  ]).catch((error) => {
    logger.error("[preloadRoute] Failed to preload billing data", error)
  })
}

const preloadTeamBillingRoute = (workspaceId: string) => {
  TeamBillingPage.preload()
  store.dispatch(ensureActiveWorkspace(workspaceId, { force: true }))
  store.dispatch(ensureMembers(workspaceId, { force: true }))
}

const preloadMap: Record<string, (() => void) | undefined> = {
  "/meetings": Meetings.preload,
  "/calls": Calls.preload,
  "/profile": Profile.preload,
  "/scheduling": Scheduling.preload,
  "/scheduling/create-your-first-meeting-type":
    CreateYourFirstMeetingType.preload,
  "/scheduling/create": MeetingTypeCreatePage.preload,
  "/scheduling/preview": ProfilePreview.preload,
  "/settings": EditProfile.preload,
  "/start-room": StartRoom.preload,
  "/privacy-policy": PrivacyPolicy.preload,
  "/terms-of-service": TermsOfService.preload,
  "/affiliate-terms": AffiliateTerms.preload,
  "/leaderboard": preloadLeaderboard,
  "/billing": preloadBillingRoute,
  "/billing/success": preloadBillingRoute,
}

export const preloadRoomInfoRoute = ({ roomId }: { roomId?: string }) => {
  RoomInfoPage.preload()

  void preloadMeetingProfile({ roomId }).catch((error) => {
    logger.error(
      "[preloadRoomInfoRoute] Failed to preload meeting profile",
      error,
    )
  })
}

export const preloadSessionSummaryRoute = ({
  sessionId,
  roomId,
}: {
  sessionId?: string
  roomId?: string
}) => {
  SessionSummary.preload()

  if (roomId) {
    void preloadMeetingProfile({ roomId }).catch((error) => {
      logger.error(
        "[preloadSessionSummaryRoute] Failed to preload meeting profile",
        error,
      )
    })
  }

  void preloadMeetingSessionDetails({ sessionId })
    .then((sessionDetails) => {
      if (roomId || !sessionDetails?.roomId) return

      return preloadMeetingProfile({ roomId: sessionDetails.roomId }).catch(
        (error) => {
          logger.error(
            "[preloadSessionSummaryRoute] Failed to preload meeting profile",
            error,
          )
        },
      )
    })
    .catch((error) => {
      logger.error(
        "[preloadSessionSummaryRoute] Failed to preload session details",
        error,
      )
    })
}

export const preloadRoute = (path: string) => {
  if (path.startsWith("/r/")) {
    RoomInfoPage.preload()
    return
  }

  if (path.startsWith("/m/")) {
    SessionSummary.preload()
    return
  }

  if (path.startsWith("/rec/")) {
    RecordingView.preload()
    return
  }

  if (path.startsWith("/team-billing/")) {
    const workspaceId = path.split("/")[2]
    if (workspaceId) {
      preloadTeamBillingRoute(workspaceId)
    }
    return
  }

  if (path.startsWith("/scheduling/edit/")) {
    MeetingTypeEditPage.preload()
    return
  }

  void preloadMap[path]?.()
}

const createRouteElement = (route: RouteConfig) => {
  const element = route.guestOnly ? (
    <LazyGuestRoute>
      <route.component />
    </LazyGuestRoute>
  ) : (
    <route.component />
  )

  const guardedElement = route.protected ? (
    <LazyProtectedRoute>{element}</LazyProtectedRoute>
  ) : (
    element
  )

  return route.mobileAllowed ? (
    guardedElement
  ) : (
    <DesktopRoute>{guardedElement}</DesktopRoute>
  )
}

export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    errorElement: <RouteErrorBoundary />,
    children: publicRoutes.map((route) => ({
      path: route.path,
      element: createRouteElement(route),
      errorElement: <RouteErrorBoundary />,
    })),
  },
  {
    element: (
      <Suspense fallback={null}>
        <AppLayout />
      </Suspense>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [...routes].map((route) => ({
      path: route.path,
      element: createRouteElement(route),
      errorElement: <RouteErrorBoundary />,
    })),
  },
  {
    path: "*",
    loader: () => {
      throw new Response(null, { status: 404, statusText: "Not Found" })
    },
    errorElement: <RouteErrorBoundary />,
  },
])
